import { findDatePresetOption } from './runtime'
import type { DashboardRuntimeDateRange, DatePresetOption } from './types'

/** Display order and friendly headers for catalog datePresetOptions groups. */
export const DATE_PRESET_GROUP_LABELS: Array<{ group: string; label: string }> = [
  { group: 'days', label: 'Days' },
  { group: 'weeks', label: 'Weeks' },
  { group: 'months', label: 'Months' },
  { group: 'quarters', label: 'Quarters' },
  { group: 'trimesters', label: 'Trimesters' },
  { group: 'calendarYear', label: 'Calendar year' },
  { group: 'financialYear', label: 'Financial year' },
]

const OTHER_GROUP_LABEL = 'Other'

export type DatePresetOptionGroup = {
  group: string
  label: string
  options: DatePresetOption[]
}

/** Groups presets by `group`, in DATE_PRESET_GROUP_LABELS order; unknown groups go last under "Other". Catalog order is kept within a group. */
export function groupDatePresetOptions(options: DatePresetOption[] | null | undefined): DatePresetOptionGroup[] {
  const byGroup = new Map<string, DatePresetOption[]>()
  for (const option of options ?? []) {
    const known = DATE_PRESET_GROUP_LABELS.some((entry) => entry.group === option.group)
    const key = known ? option.group : ''
    byGroup.set(key, [...(byGroup.get(key) ?? []), option])
  }

  const groups: DatePresetOptionGroup[] = DATE_PRESET_GROUP_LABELS
    .filter((entry) => byGroup.has(entry.group))
    .map((entry) => ({ ...entry, options: byGroup.get(entry.group) ?? [] }))
  const other = byGroup.get('')
  if (other?.length) groups.push({ group: 'other', label: OTHER_GROUP_LABEL, options: other })
  return groups
}

/** "2026-09-29" -> "29 Sep 2026"; returns the input unchanged when it is not an ISO date. */
export function formatPresetDate(value: string | null | undefined): string {
  if (!value) return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return value
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

/** "2026-07-01" -> "01-07-2026"; null when the value is not an ISO date. */
export function formatIsoDateDmy(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null
}

/**
 * Plain subtitle for the active page date filter: a preset's label as the catalog publishes it, or
 * "DD-MM-YYYY to DD-MM-YYYY" for a custom range. Null when there is no date filter, or when a
 * preset's label is not in the catalog, so callers show nothing rather than a raw preset key.
 */
export function describeDateRangeSubtitle(
  dateRange: DashboardRuntimeDateRange | null | undefined,
  datePresetOptions: DatePresetOption[] | null | undefined,
): string | null {
  if (!dateRange) return null

  if (dateRange.mode === 'preset') {
    return findDatePresetOption(datePresetOptions, dateRange.preset)?.label ?? null
  }

  if (dateRange.mode === 'custom') {
    const from = formatIsoDateDmy(dateRange.from)
    const to = formatIsoDateDmy(dateRange.to)
    if (from && to) return `${from} to ${to}`
    if (from) return `From ${from}`
    if (to) return `Until ${to}`
  }

  return null
}

export function formatPresetWindow(option: Pick<DatePresetOption, 'from' | 'to'> | null | undefined): string {
  if (!option?.from && !option?.to) return ''
  return `${formatPresetDate(option.from) || '…'} – ${formatPresetDate(option.to) || '…'}`
}

/**
 * Human-readable summary of the active page date filter, e.g.
 * "Last 30 days (31 Aug 2026 – 29 Sep 2026, as of 29 Sep 2026)".
 * Returns null when no date filter is active.
 */
export function describeDashboardDateRange(
  dateRange: DashboardRuntimeDateRange,
  datePresetOptions: DatePresetOption[] | null | undefined,
  datePresetsAsOf: string | null | undefined,
): string | null {
  if (dateRange.mode === 'preset') {
    if (!dateRange.preset) return null
    const option = findDatePresetOption(datePresetOptions, dateRange.preset)
    if (!option) return dateRange.preset
    const window = formatPresetWindow(option)
    const asOf = datePresetsAsOf ? `as of ${formatPresetDate(datePresetsAsOf)}` : ''
    const detail = [window, asOf].filter(Boolean).join(', ')
    return detail ? `${option.label} (${detail})` : option.label
  }

  if (dateRange.mode === 'custom' && (dateRange.from || dateRange.to)) {
    return `${formatPresetDate(dateRange.from) || '…'} – ${formatPresetDate(dateRange.to) || '…'}`
  }

  return null
}
